import React, { useCallback, useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import { FileTypeIcon, getFileKind } from "components/common/FileTypeIcon";
import { FileDropzone } from "components/common/FileDropzone";
import DeleteModal from "components/common/DeleteModal";
import { DateTime } from "components/common/Date";
import { formatFileSize } from "utils/fileUtils";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_FILES_PER_UPLOAD,
  ATTACHMENT_MAX_FILE_BYTES,
  AttachmentSourceType,
} from "features/Attachments/constants/attachment.constants";
import type { Attachment } from "features/Attachments/types/attachment.types";
import {
  deleteAttachment,
  getAttachments,
  uploadAttachments,
} from "features/Attachments/services/AttachmentService";

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

function uploaderName(a: Attachment): string {
  const u = a.uploader;
  if (!u) return "—";
  return [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || "—";
}

type Props = {
  sourceType: AttachmentSourceType;
  sourceId: number;
  /** Upload/delete need the parent record's own UPDATE permission. */
  canManage: boolean;
};

function AttachmentCard({
  attachment,
  canManage,
  onImageClick,
  onDelete,
}: {
  attachment: Attachment;
  canManage: boolean;
  onImageClick?: () => void;
  onDelete: () => void;
}) {
  const { color, kindLabel, isImage } = getFileKind(attachment.fileName);
  const [imgError, setImgError] = useState(false);

  return (
    <Col xs={6} sm={4} md={3} xl={2}>
      <Card className="border shadow-none h-100 mb-0">
        <CardBody className="p-3 d-flex flex-column">
          {isImage && !imgError ? (
            <button
              type="button"
              className="d-block rounded overflow-hidden bg-light mb-3 border-0 p-0 w-100"
              style={{ aspectRatio: "4 / 3", cursor: onImageClick ? "zoom-in" : "default" }}
              onClick={onImageClick}
              disabled={!onImageClick}
            >
              <img
                src={attachment.url}
                alt=""
                className="w-100 h-100"
                style={{ objectFit: "cover" }}
                onError={() => setImgError(true)}
              />
            </button>
          ) : (
            <div
              className={`rounded d-flex align-items-center justify-content-center mb-3 bg-${color}-subtle`}
              style={{ aspectRatio: "4 / 3", minHeight: "120px" }}
            >
              <FileTypeIcon
                filename={attachment.fileName}
                className={`text-${color}`}
                style={{ fontSize: "2.5rem" }}
              />
            </div>
          )}

          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
              <Badge
                color="secondary"
                className="fw-normal text-uppercase bg-body-secondary text-muted border"
                pill
              >
                {kindLabel}
              </Badge>
              <span className="text-muted small">
                {formatFileSize(attachment.fileSize)}
              </span>
            </div>
            <p
              className="fw-medium text-body mb-1 text-break small"
              title={attachment.fileName}
            >
              {attachment.fileName}
            </p>
            {attachment.description ? (
              <p className="text-muted small mb-2 text-break">
                {attachment.description}
              </p>
            ) : null}
            <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
              {uploaderName(attachment)} ·{" "}
              <DateTime date={attachment.created_at} />
            </p>
          </div>

          <div className="d-flex gap-2 mt-3 pt-1">
            <Button
              tag="a"
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              outline
              size="sm"
              className="flex-grow-1"
            >
              <i className="ri-external-link-line align-middle me-1" />
              Open
            </Button>
            {canManage ? (
              <Button color="light" size="sm" title="Delete" onClick={onDelete}>
                <i className="ri-delete-bin-line align-middle text-danger" />
              </Button>
            ) : null}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
}

export default function AttachmentsTab({ sourceType, sourceId, canManage }: Props) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAttachments({ sourceType, sourceId, limit: 100 });
      if (res.success) {
        setAttachments(res.data.rows);
      }
    } catch (err) {
      console.warn(err);
      toast.error("Could not load attachments");
    } finally {
      setLoading(false);
    }
  }, [sourceType, sourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const imageAttachments = useMemo(
    () => attachments.filter((a) => getFileKind(a.fileName).isImage),
    [attachments],
  );

  const closeUploadModal = () => {
    if (uploading) return;
    setUploadOpen(false);
    setPendingFiles([]);
    setDescription("");
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0) {
      toast.error("Choose at least one file to upload.");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAttachments({
        sourceType,
        sourceId,
        description: description.trim() || undefined,
        files: pendingFiles,
      });
      if (res.success) {
        toast.success(res.message ?? "Uploaded.");
        closeUploadModal();
        await load();
      } else {
        toast.error(res.message ?? "Upload failed.");
      }
    } catch (err) {
      toast.error(axiosMessage(err, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteAttachment(deleteTarget.id);
      if (res.success) {
        toast.success(res.message ?? "Deleted.");
        setDeleteTarget(null);
        await load();
      } else {
        toast.error(res.message ?? "Could not delete.");
      }
    } catch (err) {
      toast.error(axiosMessage(err, "Could not delete."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Row>
      <Col>
        <Card className="border shadow-none">
          <CardBody className="p-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
              <div>
                <h6 className="mb-1 text-uppercase text-muted">Attachments</h6>
                <p className="text-muted small mb-0">
                  Reference files attached to this record.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                {attachments.length > 0 ? (
                  <Badge color="primary" pill className="fs-12 px-3 py-2">
                    {attachments.length}{" "}
                    {attachments.length === 1 ? "file" : "files"}
                  </Badge>
                ) : null}
                {canManage ? (
                  <Button color="primary" size="sm" onClick={() => setUploadOpen(true)}>
                    <i className="ri-upload-2-line align-bottom me-1" />
                    Upload
                  </Button>
                ) : null}
              </div>
            </div>

            {loading ? (
              <p className="text-muted mb-0">Loading…</p>
            ) : attachments.length === 0 ? (
              <div className="text-center py-5 px-3 rounded border border-dashed">
                <div className="avatar-lg mx-auto mb-3">
                  <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-20">
                    <i className="ri-folder-open-line" />
                  </span>
                </div>
                <h6 className="mb-1">No attachments</h6>
                <p
                  className="text-muted small mb-0 mx-auto"
                  style={{ maxWidth: "320px" }}
                >
                  {canManage
                    ? "Upload reference files to attach them to this record."
                    : "No files have been attached to this record yet."}
                </p>
              </div>
            ) : (
              <Row className="g-3">
                {attachments.map((a) => {
                  const imageIdx = getFileKind(a.fileName).isImage
                    ? imageAttachments.indexOf(a)
                    : -1;
                  return (
                    <AttachmentCard
                      key={a.id}
                      attachment={a}
                      canManage={canManage}
                      onImageClick={
                        imageIdx >= 0
                          ? () => {
                              setLightboxIndex(imageIdx);
                              setLightboxOpen(true);
                            }
                          : undefined
                      }
                      onDelete={() => setDeleteTarget(a)}
                    />
                  );
                })}
              </Row>
            )}
          </CardBody>
        </Card>
      </Col>

      {imageAttachments.length > 0 ? (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={imageAttachments.map((a) => ({ src: a.url }))}
        />
      ) : null}

      <DeleteModal
        show={Boolean(deleteTarget)}
        onDeleteClick={() => void handleDelete()}
        onCloseClick={() => !deleting && setDeleteTarget(null)}
        recordId={deleteTarget?.fileName}
      />

      <Modal isOpen={uploadOpen} toggle={closeUploadModal} centered size="lg">
        <ModalHeader toggle={closeUploadModal}>Upload attachments</ModalHeader>
        <ModalBody>
          <FileDropzone
            accept={ATTACHMENT_ACCEPT}
            maxSize={ATTACHMENT_MAX_FILE_BYTES}
            multiple
            disabled={uploading}
            description={`Images, PDF, Office docs, text, or zip — up to ${ATTACHMENT_MAX_FILES_PER_UPLOAD} files, 20 MB each`}
            onDropAccepted={(files) =>
              setPendingFiles((prev) =>
                [...prev, ...files].slice(0, ATTACHMENT_MAX_FILES_PER_UPLOAD),
              )
            }
          />

          {pendingFiles.length > 0 ? (
            <ul className="list-unstyled mt-3 mb-0 vstack gap-2">
              {pendingFiles.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
                >
                  <span className="d-flex align-items-center gap-2 min-w-0">
                    <FileTypeIcon filename={f.name} />
                    <span className="text-truncate">{f.name}</span>
                    <span className="text-muted small flex-shrink-0">
                      {formatFileSize(f.size)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    disabled={uploading}
                    onClick={() =>
                      setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))
                    }
                  >
                    <i className="ri-close-line" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-3">
            <Label className="form-label" for="attachment-description">
              Description{" "}
              <span className="text-muted fw-normal">
                (optional, applies to all files in this upload)
              </span>
            </Label>
            <Input
              id="attachment-description"
              type="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
            />
          </div>

          <div className="d-flex gap-2 justify-content-end mt-3">
            <Button color="light" onClick={closeUploadModal} disabled={uploading}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={uploading || pendingFiles.length === 0}
              onClick={() => void handleUpload()}
            >
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Row>
  );
}
